import React, { useState, useEffect } from 'react';
import { confirmManager, resendManagerCode } from '../cognito';
import { getManagerByEmail, getCEsForManager, getCEByEmail, SERVICE_MANAGERS } from '../supportConfig';

// ── Role types creatable from this page ───────────────────────
// super_admin → Admin, Service Manager, Support Engineer
// admin       → Service Manager, Support Engineer
// service_manager → Service Manager, Support Engineer
// customer_engineer → Support Engineer only
const ROLE_TYPES = [
  { id: 'admin',             label: 'Admin',           desc: 'Full admin dashboard access',                   icon: '🔑' },
  { id: 'service_manager',   label: 'Service Manager', desc: 'Manages a region, CE team, tickets & pricing',  icon: '👤' },
  { id: 'customer_engineer', label: 'Support Engineer',desc: 'Handles tickets, manages field engineers',       icon: '🔧' },
];

// Who can create whom
const CREATABLE_ROLES = {
  super_admin:       ['admin', 'service_manager', 'customer_engineer'],
  admin:             ['service_manager', 'customer_engineer'],
  service_manager:   ['service_manager', 'customer_engineer'],
  customer_engineer: ['customer_engineer'],
};

// Display label overrides per viewer role
const ROLE_LABEL_OVERRIDES = {
  service_manager:   { customer_engineer: 'Support Engineer' },
  customer_engineer: { customer_engineer: 'Support Engineer' },
};

// ── All permissions ───────────────────────────────────────────────────────────
const ALL_PERMISSIONS = [
  { id: 'onboard_customers', label: 'Onboard Customers',          desc: 'Register new end-customers and assign devices', icon: '👥', color: '#007AFF' },
  { id: 'add_engineers',     label: 'Add Customer Engineers',     desc: 'Create and manage customer engineer accounts',  icon: '🔧', color: '#AF52DE' },
  { id: 'manage_coupons',    label: 'Manage Coupons',             desc: 'Create, edit and deactivate recharge coupons',  icon: '🏷️', color: '#FF9F0A' },
  { id: 'recharge',          label: 'Recharge for End Customers', desc: 'Send RC commands and process recharges',        icon: '💳', color: '#34C759' },
  { id: 'view_analytics',    label: 'View Analytics',             desc: 'Access device analytics and usage dashboards',  icon: '📊', color: '#0ea5e9' },
  { id: 'manage_plans',      label: 'Manage Plan Pricing',        desc: 'Set custom pricing per model and region',       icon: '💰', color: '#f59e0b' },
];

// Support Engineers only get these 2 permissions
const CE_PERMISSIONS = ['manage_coupons', 'recharge'];

const PERMISSIONS = ALL_PERMISSIONS;

// ── Generate a strong temp password ──────────────────────────────────────────
function generatePassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '@#!&*^';
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pwd = [pick(upper), pick(lower), pick(digits), pick(special)];
  const all = upper + lower + digits + special;
  for (let i = pwd.length; i < 12; i++) pwd.push(pick(all));
  for (let i = pwd.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pwd[i], pwd[j]] = [pwd[j], pwd[i]];
  }
  return pwd.join('');
}

// ── Success Popup ────────────────────────────────────────────────────────────
function SuccessPopup({ email, password, permissions, needsConfirm, roleLabel, onClose }) {
  const [copied, setCopied] = useState(false);

  const credentialsText = [
    `Account Created Successfully`,
    `────────────────────────────────`,
    `Email:    ${email}`,
    `Password: ${password}`,
    `Role:     ${roleLabel}`,
    ``,
    `Permissions:`,
    ...permissions.map(p => `  ✓ ${PERMISSIONS.find(x => x.id === p)?.label || p}`),
    ``,
    `Login URL: ${window.location.origin}`,
    needsConfirm
      ? `Note: User must verify email with the code sent to their inbox before logging in.`
      : `Note: Account is ready to use.`,
  ].join('\n');

  const handleCopy = () => {
    navigator.clipboard.writeText(credentialsText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(0,0,0,0.5)', backdropFilter:'blur(12px)',
    }}>
      <div style={{
        background:'#fff', borderRadius:24, padding:'32px 28px 28px',
        width:400, maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 24px 80px rgba(0,0,0,0.3)',
        animation:'popIn .3s cubic-bezier(.4,0,.2,1)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
      }}>
        <div style={{
          width:72, height:72, borderRadius:'50%', margin:'0 auto 20px',
          background:'linear-gradient(135deg,#34C759,#248A3D)',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 8px 32px rgba(52,199,89,.45)',
          animation:'popIn .4s cubic-bezier(.4,0,.2,1)',
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
            <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ fontSize:20, fontWeight:800, color:'#1c1c1e', marginBottom:4 }}>
            {roleLabel} Created!
          </div>
          <div style={{ fontSize:13, color:'#8E8E93' }}>
            Account is ready. Credentials were also emailed to {email}.
          </div>
        </div>

        <div style={{
          background:'#f8f9fa', borderRadius:16, padding:'18px',
          border:'1px solid rgba(0,0,0,.06)', marginBottom:16,
        }}>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div>
              <div style={{ fontSize:10, color:'#8E8E93', fontWeight:700, letterSpacing:.5, marginBottom:4 }}>
                EMAIL / USERNAME
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:'#1c1c1e',
                fontFamily:'"SF Mono",monospace', wordBreak:'break-all' }}>{email}</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#8E8E93', fontWeight:700, letterSpacing:.5, marginBottom:4 }}>
                PASSWORD
              </div>
              <div style={{
                fontSize:16, fontWeight:800, color:'#DC2626',
                fontFamily:'"SF Mono",monospace', letterSpacing:.5,
                background:'rgba(220,38,38,.06)', padding:'8px 12px', borderRadius:10,
                border:'1px solid rgba(220,38,38,.15)',
              }}>{password}</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#8E8E93', fontWeight:700, letterSpacing:.5, marginBottom:6 }}>
                PERMISSIONS
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {permissions.map(pId => {
                  const p = PERMISSIONS.find(x => x.id === pId);
                  return p ? (
                    <span key={pId} style={{
                      fontSize:10, fontWeight:600, padding:'3px 10px', borderRadius:20,
                      background:`${p.color}12`, color:p.color,
                      display:'inline-flex', alignItems:'center', gap:4,
                    }}>
                      <span style={{ fontSize:12 }}>{p.icon}</span> {p.label}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          {needsConfirm && (
            <div style={{
              marginTop:14, padding:'8px 12px', borderRadius:10,
              background:'rgba(255,159,10,.08)', border:'1px solid rgba(255,159,10,.15)',
              fontSize:11, color:'#CC7A00', lineHeight:1.5,
            }}>
              ⚠️ A verification code has been sent to {email}. The user must enter this code
              before they can log in.
            </div>
          )}

          <div style={{
            marginTop:10, padding:'8px 12px', borderRadius:10,
            background:'rgba(0,122,255,.06)', border:'1px solid rgba(0,122,255,.12)',
            fontSize:11, color:'#0056d3', lineHeight:1.5,
          }}>
            📋 This is the only time the password is shown. Copy it now.
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button onClick={handleCopy} style={{
            flex:1, padding:'13px', borderRadius:14, border:'none',
            background: copied
              ? 'linear-gradient(135deg,#34C759,#248A3D)'
              : 'linear-gradient(135deg,#007AFF,#0056d3)',
            color:'#fff', fontWeight:700, fontSize:14,
            cursor:'pointer', fontFamily:'inherit',
            boxShadow: copied ? '0 4px 16px rgba(52,199,89,.4)' : '0 4px 16px rgba(0,122,255,.35)',
            display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            transition:'all .2s',
          }}>
            {copied ? '✅ Copied!' : '📋 Copy Credentials'}
          </button>
          <button onClick={onClose} style={{
            padding:'13px 20px', borderRadius:14,
            border:'1.5px solid rgba(0,0,0,.1)', background:'#fff',
            color:'#636366', fontWeight:600, fontSize:14,
            cursor:'pointer', fontFamily:'inherit',
          }}>Close</button>
        </div>
      </div>
      <style>{`
        @keyframes popIn { 0%{transform:scale(.6);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
      `}</style>
    </div>
  );
}

// ── Confirm Code Modal ───────────────────────────────────────────────────────
function ConfirmModal({ email, onConfirmed, onClose }) {
  const [code, setCode]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [resent, setResent]   = useState(false);

  const handleConfirm = async () => {
    if (!code.trim()) { setError('Enter the 6-digit code'); return; }
    setLoading(true); setError('');
    try {
      await confirmManager(email, code.trim());
      onConfirmed();
    } catch (err) {
      setError(err.message || 'Invalid code');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    try {
      await resendManagerCode(email);
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to resend');
    }
  };

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(0,0,0,0.5)', backdropFilter:'blur(12px)',
    }}>
      <div style={{
        background:'#fff', borderRadius:20, padding:'28px 24px',
        width:360, maxWidth:'92vw',
        boxShadow:'0 24px 80px rgba(0,0,0,0.3)',
        fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
      }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#1c1c1e', marginBottom:4 }}>
          Verify Email
        </div>
        <div style={{ fontSize:13, color:'#8E8E93', marginBottom:18, lineHeight:1.5 }}>
          Enter the 6-digit code sent to <strong>{email}</strong>
        </div>

        <input type="text" value={code} maxLength={6}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          style={{
            width:'100%', padding:'14px', borderRadius:12, border:'1.5px solid rgba(0,0,0,.1)',
            fontSize:24, fontWeight:800, textAlign:'center', letterSpacing:8,
            fontFamily:'"SF Mono",monospace', background:'#fafafa',
            outline:'none', boxSizing:'border-box', marginBottom:12,
          }}/>

        {error && (
          <div style={{ fontSize:12, color:'#FF3B30', fontWeight:600, marginBottom:10, textAlign:'center' }}>
            ⚠️ {error}
          </div>
        )}

        <button onClick={handleConfirm} disabled={loading} style={{
          width:'100%', padding:'13px', borderRadius:14, border:'none',
          background: loading ? '#c7c7cc' : 'linear-gradient(135deg,#DC2626,#B91C1C)',
          color:'#fff', fontWeight:700, fontSize:15,
          cursor: loading ? 'not-allowed' : 'pointer', fontFamily:'inherit',
          boxShadow: loading ? 'none' : '0 4px 16px rgba(220,38,38,.4)',
          marginBottom:8,
        }}>
          {loading ? 'Verifying…' : '✅ Confirm'}
        </button>

        <div style={{ display:'flex', justifyContent:'space-between' }}>
          <button onClick={handleResend} style={{
            background:'none', border:'none', color:'#007AFF', fontSize:12,
            fontWeight:600, cursor:'pointer', fontFamily:'inherit',
          }}>
            {resent ? '✅ Code Resent' : '↻ Resend Code'}
          </button>
          <button onClick={onClose} style={{
            background:'none', border:'none', color:'#8E8E93', fontSize:12,
            fontWeight:600, cursor:'pointer', fontFamily:'inherit',
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Monthly Coupon Panel — for Managers only ─────────────────────────────────
// Manager sets a monthly discount % for their team (Support Engineers &
// Field Engineers). Each month Claude auto-generates a fresh coupon with
// that %. The manager can update the % at any time; it applies from the
// next generation onward. Existing active monthlies are listed below.
function MonthlyCouponPanel({ session }) {
  const email = session?.getIdToken()?.payload?.email || '';
  const name  = session?.getIdToken()?.payload?.name || email.split('@')[0];

  const THIS_MONTH = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  };

  const [coupons, setCoupons]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('all'); // 'all' or specific email
  const [monthlyPct, setMonthlyPct]   = useState(10);
  const [maxUses, setMaxUses]         = useState(50);
  const [note, setNote]               = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(''); // always start clean — no stale error on mount
  const [copiedCode, setCopiedCode]   = useState('');
  const [successMsg, setSuccessMsg]   = useState('');

  // Load hardcoded team members from supportConfig.
  // Works for both a manager login and a CE login (finds their manager's team).
  useEffect(() => {
    if (!email) return;
    let mgr = getManagerByEmail(email); // case 1: logged-in user IS a manager
    if (!mgr) {
      // case 2: logged-in user is a CE — look up their manager by manager_id
      const ce = getCEByEmail(email);
      if (ce) {
        mgr = SERVICE_MANAGERS.find(m => m.id === ce.manager_id) || null;
      }
    }
    if (mgr) {
      const ces = getCEsForManager(mgr.id).map(ce => ({
        email: ce.email,
        fullName: ce.name,
        code: ce.code,
        role: 'customer_engineer',
      }));
      setTeamMembers(ces);
    }
  }, [email]); // eslint-disable-line

  const fetchCoupons = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/coupons?owner_email=${encodeURIComponent(email)}&type=monthly`);
      const data = await res.json();
      setCoupons(Array.isArray(data) ? data : []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCoupons(); }, [email]); // eslint-disable-line

  // Generate (or regenerate) this month's coupon — either for the whole team or one person
  const handleGenerate = async () => {
    setError(''); setSuccessMsg('');
    if (!monthlyPct || monthlyPct <= 0 || monthlyPct > 100) {
      setError('Discount must be between 1 and 100%'); return;
    }
    setSaving(true);
    try {
      const month = THIS_MONTH();
      const now = new Date();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth()+1, 0);
      const daysLeft = Math.max(1, Math.ceil((endOfMonth - now) / (1000*60*60*24)));

      const memberLabel = selectedMember === 'all'
        ? 'Whole Team'
        : teamMembers.find(m => m.email === selectedMember)?.fullName || selectedMember;

      const res = await fetch('/api/coupons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerEmail: email,
          ownerName: name,
          discountPercent: monthlyPct,
          validityDays: daysLeft,
          maxUses,
          note: note || `Monthly coupon (${memberLabel}) — ${new Date().toLocaleString('default',{month:'long',year:'numeric'})}`,
          type: 'monthly',
          month,
          // If targeting a specific member, pass their email so backend can record it
          assignedTo: selectedMember === 'all' ? null : selectedMember,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(()=>({}));
        throw new Error(e.error || 'Failed to generate coupon');
      }
      const forLabel = selectedMember === 'all' ? 'your whole team' : memberLabel;
      setSuccessMsg(`✅ ${monthlyPct}% coupon generated for ${forLabel} (${new Date().toLocaleString('default',{month:'long',year:'numeric'})})!`);
      setNote('');
      setSelectedMember('all');
      fetchCoupons();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Deactivate coupon ${code}?`)) return;
    try {
      await fetch(`/api/coupons?code=${encodeURIComponent(code)}`, { method: 'DELETE' });
      fetchCoupons();
    } catch { /* ignore */ }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 2000);
    });
  };

  const inputStyle = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:'1.5px solid rgba(0,0,0,.1)', fontSize:13,
    fontFamily:'inherit', background:'#fafafa', outline:'none', boxSizing:'border-box',
  };

  const currentMonth = new Date().toLocaleString('default',{month:'long', year:'numeric'});

  return (
    <div style={{ marginTop:28 }}>
      <div style={{ fontSize:16, fontWeight:800, color:'#1c1c1e', marginBottom:4 }}>🏷️ Monthly Team Coupon</div>
      <div style={{ fontSize:12, color:'#8E8E93', marginBottom:16 }}>
        Set this month's discount % for your Support Engineers &amp; Field Engineers.
        Generate a coupon each month — it's valid until month-end and can be used by your team.
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start' }}>
        {/* Generate form */}
        <div style={{ background:'#fff', borderRadius:18, padding:20, border:'1px solid rgba(0,0,0,.06)',
          boxShadow:'0 2px 14px rgba(0,0,0,.06)' }}>

          <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e', marginBottom:14,
            display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:18 }}>📅</span> {currentMonth}
          </div>

          {/* Team member selector */}
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>
              For Team Member
            </label>
            <select
              value={selectedMember}
              onChange={e => setSelectedMember(e.target.value)}
              style={{
                width:'100%', padding:'10px 12px', borderRadius:10,
                border:'1.5px solid rgba(0,0,0,.1)', fontSize:13,
                fontFamily:'inherit', background:'#fafafa', outline:'none',
                boxSizing:'border-box', cursor:'pointer',
              }}
            >
              <option value="all">👥 Whole Team (all {teamMembers.length} members)</option>
              {teamMembers.map(m => (
                <option key={m.email} value={m.email}>
                  🔧 {m.code ? `${m.code} · ` : ''}{m.fullName || m.email}
                </option>
              ))}
            </select>
            {selectedMember !== 'all' && (() => {
              const m = teamMembers.find(x => x.email === selectedMember);
              return (
                <div style={{ marginTop:6, padding:'7px 10px', borderRadius:8,
                  background:'rgba(0,122,255,.06)', border:'1px solid rgba(0,122,255,.12)',
                  display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{
                    width:28, height:28, borderRadius:8, flexShrink:0,
                    background:'linear-gradient(135deg,#DC2626,#EF4444)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#fff', fontSize:11, fontWeight:800,
                  }}>{(m?.fullName||'?')[0].toUpperCase()}</div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#1c1c1e' }}>
                      {m?.code && <span style={{ color:'#8E8E93', marginRight:4 }}>{m.code} ·</span>}
                      {m?.fullName}
                    </div>
                    <div style={{ fontSize:10, color:'#007AFF', fontWeight:600 }}>Coupon assigned to this person only</div>
                  </div>
                </div>
              );
            })()}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>
                Discount % *
              </label>
              <input type="number" min="1" max="100" value={monthlyPct}
                onChange={e=>setMonthlyPct(Number(e.target.value))} style={inputStyle}
                placeholder="e.g. 15"/>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>
                Max Uses
              </label>
              <input type="number" min="1" value={maxUses}
                onChange={e=>setMaxUses(Number(e.target.value))} style={inputStyle}/>
            </div>
          </div>

          <div style={{ marginBottom:8, padding:'8px 12px', borderRadius:10,
            background:'rgba(0,122,255,.05)', border:'1px solid rgba(0,122,255,.12)',
            fontSize:11, color:'#0056d3', lineHeight:1.6 }}>
            ℹ️ Validity is auto-set to remaining days in {currentMonth}.
            Each month click Generate to issue a fresh coupon with the updated %.
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>Note (optional)</label>
            <input type="text" value={note} onChange={e=>setNote(e.target.value)}
              placeholder={`e.g. ${currentMonth} team discount`} style={inputStyle}/>
          </div>

          {error && (
            <div style={{ fontSize:12, color:'#FF3B30', fontWeight:600, marginBottom:12,
              padding:'9px 12px', background:'rgba(255,59,48,.06)', borderRadius:9 }}>{error}</div>
          )}
          {successMsg && (
            <div style={{ fontSize:12, color:'#248A3D', fontWeight:600, marginBottom:12,
              padding:'9px 12px', background:'rgba(52,199,89,.06)', borderRadius:9 }}>{successMsg}</div>
          )}

          <button onClick={handleGenerate} disabled={saving} style={{
            width:'100%', padding:12, borderRadius:12, border:'none',
            background: saving ? '#c7c7cc' : 'linear-gradient(135deg,#FF9F0A,#CC7A00)',
            color:'#fff', fontWeight:700, fontSize:14, cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily:'inherit', boxShadow: saving ? 'none' : '0 4px 14px rgba(255,159,10,.35)',
          }}>{saving ? 'Generating…' : `🏷️ Generate ${currentMonth} Coupon`}</button>
        </div>

        {/* Active monthlies list */}
        <div style={{ background:'#fff', borderRadius:18, padding:20, border:'1px solid rgba(0,0,0,.06)',
          boxShadow:'0 2px 14px rgba(0,0,0,.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>
              {coupons.length} monthly coupon{coupons.length!==1?'s':''}
            </div>
            <button onClick={fetchCoupons} disabled={loading} style={{
              padding:'5px 10px', borderRadius:8, border:'1px solid rgba(0,0,0,.1)',
              background:'#fff', color:'#007AFF', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
            }}>{loading ? 'Loading…' : '↻ Refresh'}</button>
          </div>
          {coupons.length === 0 ? (
            <div style={{ textAlign:'center', padding:24, color:'#8E8E93', fontSize:12 }}>
              No monthly coupons yet — generate one for this month
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:340, overflowY:'auto' }}>
              {coupons.map(c => {
                const expired = new Date(c.expiresAt) < new Date();
                return (
                  <div key={c.code} style={{ padding:'11px 13px', borderRadius:12,
                    border: expired ? '1px solid rgba(255,59,48,.2)' : '1px solid rgba(52,199,89,.2)',
                    background: expired ? 'rgba(255,59,48,.03)' : 'rgba(52,199,89,.02)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                      <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:800, color:'#1c1c1e' }}>{c.code}</span>
                      <span style={{ fontSize:13, fontWeight:800, color:'#FF9F0A' }}>{c.discountPercent}% off</span>
                    </div>
                    <div style={{ fontSize:10, color:'#8E8E93', marginBottom:6 }}>
                      {c.note ? `${c.note} · ` : ''}{c.usedCount}/{c.maxUses} used ·
                      {' '}{expired ? '❌ Expired' : `✅ Valid till ${new Date(c.expiresAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}`}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={()=>handleCopy(c.code)} style={{
                        flex:1, padding:'5px 0', borderRadius:8, border:'none',
                        background: copiedCode===c.code ? 'rgba(52,199,89,.12)' : 'rgba(0,122,255,.08)',
                        color: copiedCode===c.code ? '#34C759' : '#007AFF',
                        fontSize:10, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                      }}>{copiedCode===c.code ? '✓ Copied' : 'Copy Code'}</button>
                      <button onClick={()=>handleDelete(c.code)} style={{
                        padding:'5px 10px', borderRadius:8, border:'none',
                        background:'rgba(255,59,48,.06)', color:'#FF3B30',
                        fontSize:10, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
                      }}>Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main RoleManagement Page — admin can create Service Manager / Support
// Engineer / Super Admin accounts; a manager visiting this page can only
// create Support Engineers (their own permission set is fixed/hidden).
// ═══════════════════════════════════════════════════════════════════════
export default function RoleManagement({ session, role }) {
  const isSuperAdmin = role === 'super_admin';
  const isAdmin   = role === 'admin' || role === 'super_admin';
  const isManager = role === 'service_manager';
  const isCE      = role === 'customer_engineer';

  // Roles this user is allowed to create
  const creatableRoles = CREATABLE_ROLES[role] || ['field_engineer'];

  const [email, setEmail]             = useState('');
  const [fullName, setFullName]       = useState('');
  const [password, setPassword]       = useState(() => generatePassword());
  // Super admin defaults to 'service_manager' so the Admin button isn't accidentally
  // the first thing selected — they must explicitly click Admin to create one.
  const defaultRole = (role === 'super_admin') ? 'service_manager' : creatableRoles[0];
  const [selectedRole, setSelectedRole] = useState(defaultRole);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [creating, setCreating]       = useState(false);
  const [error, setError]             = useState('');
  const [successData, setSuccessData] = useState(null);
  const [accounts, setAccounts]       = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(null);

  // Clear any stale error on mount so previous failed attempts never bleed through
  useEffect(() => { setError(''); }, []);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await fetch('/api/accounts');
      const data = await res.json();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (e) { /* ignore */ }
    finally { setLoadingAccounts(false); }
  };

  useEffect(() => { fetchAccounts(); }, []);

  const togglePerm = (id) => {
    setSelectedPerms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const roleLabel = (r) => ROLE_TYPES.find(rt => rt.id === r)?.label || r;

  const handleCreate = async () => {
    setError('');
    if (!email.trim()) { setError('Email is required'); return; }
    if (!email.includes('@')) { setError('Enter a valid email address'); return; }
    // field_engineer and customer_engineer need no extra permissions
    const noPermsNeeded = ['admin', 'customer_engineer', 'field_engineer'];
    if (!noPermsNeeded.includes(selectedRole) && selectedPerms.length === 0) {
      setError('Select at least one permission'); return;
    }

    setCreating(true);
    try {
      // Role to create is whatever the selector shows — already gated by creatableRoles
      const roleToCreate = selectedRole;
      const res = await fetch('/api/admin-create-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          fullName: fullName.trim(),
          role: roleToCreate,
          permissions: [...selectedPerms],
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        throw { message: result.error, code: result.code };
      }

      // Show success popup — account is ready immediately, no code needed
      setSuccessData({
        email: email.trim().toLowerCase(),
        password,
        permissions: [...selectedPerms],
        needsConfirm: false,
        roleLabel: roleLabel(roleToCreate),
      });

      // Email the credentials to the new account (same Rust email service used for tamper alerts)
      fetch('/api/send-manager-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          fullName: fullName.trim(),
          permissions: selectedPerms.map(id => PERMISSIONS.find(p => p.id === id)?.label || id),
          needsConfirm: false,
        }),
      }).catch(e => console.error('Failed to send account credentials email:', e));

      // Reset form
      setEmail('');
      setFullName('');
      setPassword(generatePassword());
      setSelectedPerms([]);

      // Refresh list
      fetchAccounts();

    } catch (err) {
      const msg = err.message || err.code || 'Failed to create account';
      if (msg.includes('UsernameExistsException') || err.code === 'UsernameExistsException') {
        setError('A user with this email already exists');
      } else if (msg.toLowerCase().includes('password')) {
        setError('Password does not meet policy: min 8 chars, upper + lower + digit + special');
      } else {
        setError(msg);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmed = () => {
    setConfirmEmail(null);
    fetchAccounts();
  };

  const handleRemoveManager = async (emailToRemove) => {
    if (!window.confirm(`Remove ${emailToRemove} from the list? (This only hides the record here — it does not delete the Cognito login.)`)) return;
    try {
      await fetch(`/api/accounts?email=${encodeURIComponent(emailToRemove)}`, { method: 'DELETE' });
      fetchAccounts();
    } catch { /* ignore */ }
  };

  const inputStyle = {
    width:'100%', padding:'12px 14px', borderRadius:12,
    border:'1.5px solid rgba(0,0,0,.1)', fontSize:14,
    fontFamily:'inherit', background:'#fafafa',
    outline:'none', boxSizing:'border-box',
  };

  return (
    <div style={{
      fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
      maxWidth:960, margin:'0 auto',
    }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spinAnim { to{transform:rotate(360deg)} }
      `}</style>

      <div style={{ marginBottom:24 }}>
      <div style={{ fontSize:22, fontWeight:800, color:'#1c1c1e', letterSpacing:-.4 }}>
      👤 Role Management
      </div>
      <div style={{ fontSize:13, color:'#8E8E93', marginTop:2 }}>
        {isAdmin   && 'Create manager, support engineer & admin accounts'}
        {isManager && 'Create Support Engineer & Field Engineer accounts for your team'}
        {isCE      && 'Create Field Engineer accounts for your team'}
      </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start' }}>

        {/* ── LEFT: Create Account Form ── */}
        <div style={{
          background:'#fff', borderRadius:20, padding:'24px',
          border:'1px solid rgba(0,0,0,.06)',
          boxShadow:'0 2px 16px rgba(0,0,0,.07)',
          animation:'fadeUp .35s ease',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <div style={{
              width:42, height:42, borderRadius:13,
              background: selectedRole === 'admin'
                ? 'linear-gradient(135deg,#DC2626,#991B1B)'
                : 'linear-gradient(135deg,#DC2626,#EF4444)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:20,
            }}>{ROLE_TYPES.find(r=>r.id===selectedRole)?.icon || '👤'}</div>
            <div>
              <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e' }}>
                Create New {isCE ? 'Field Engineer' : selectedRole === 'admin' ? 'Admin' : roleLabel(selectedRole)}
              </div>
              <div style={{ fontSize:11, color:'#8E8E93' }}>Cognito account with custom permissions</div>
            </div>
          </div>

          {/* Role selector — admin only; managers can only create Support Engineers */}
          {/* Role selector — always shown, filtered to this user's creatable roles */}
          {creatableRoles.length >= 1 && (
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#48484a', display:'block', marginBottom:6 }}>
                Account Type *
              </label>

              {/* Super Admin only: Admin button shown prominently on its own row */}
              {isSuperAdmin && (
                <div style={{ marginBottom:8 }}>
                  {(() => {
                    const rt = ROLE_TYPES.find(r => r.id === 'admin');
                    const active = selectedRole === 'admin';
                    return (
                      <button onClick={() => { setSelectedRole('admin'); setSelectedPerms([]); setError(''); }} style={{
                        width:'100%', padding:'10px 12px', borderRadius:12, cursor:'pointer',
                        border: active ? '2px solid #DC2626' : '1.5px solid rgba(0,0,0,.08)',
                        background: active ? 'rgba(220,38,38,.05)' : '#fafafa',
                        textAlign:'center', fontFamily:'inherit',
                        display:'flex', alignItems:'center', gap:10,
                      }}>
                        <span style={{ fontSize:20 }}>{rt.icon}</span>
                        <div style={{ textAlign:'left' }}>
                          <div style={{ fontSize:12, fontWeight:700, color: active ? '#DC2626' : '#48484a' }}>{rt.label}</div>
                          <div style={{ fontSize:10, color:'#8E8E93', marginTop:1 }}>{rt.desc}</div>
                        </div>
                      </button>
                    );
                  })()}
                </div>
              )}

              {/* Other role buttons (Service Manager, Support Engineer) */}
              <div style={{ display:'flex', gap:8 }}>
                {ROLE_TYPES.filter(rt => creatableRoles.includes(rt.id) && rt.id !== 'admin').map(rt => {
                  const active = selectedRole === rt.id;
                  const displayLabel = ROLE_LABEL_OVERRIDES[role]?.[rt.id] || rt.label;
                  return (
                    <button key={rt.id} onClick={() => {
                      setSelectedRole(rt.id);
                      if (rt.id === 'customer_engineer') setSelectedPerms([...CE_PERMISSIONS]);
                      else setSelectedPerms([]);
                      setError('');
                    }} style={{
                      flex:1, padding:'10px 8px', borderRadius:12, cursor:'pointer',
                      border: active ? '2px solid #DC2626' : '1.5px solid rgba(0,0,0,.08)',
                      background: active ? 'rgba(220,38,38,.05)' : '#fafafa',
                      textAlign:'center', fontFamily:'inherit',
                    }}>
                      <div style={{ fontSize:18, marginBottom:3 }}>{rt.icon}</div>
                      <div style={{ fontSize:11, fontWeight:700, color: active ? '#DC2626' : '#48484a' }}>{displayLabel}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize:10, color:'#8E8E93', marginTop:6 }}>
                {ROLE_TYPES.find(r=>r.id===selectedRole)?.desc}
              </div>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>
              Email Address *
            </label>
            <input type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="manager@company.com"
              style={inputStyle}/>
          </div>

          {/* Full Name */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>
              Full Name
            </label>
            <input type="text" value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="e.g. Ayesha Khan"
              style={inputStyle}/>
          </div>

          {/* Password — hide for CE (Support Engineer creating Field Engineer); always auto-generated */}
          {!isCE && (
          <div style={{ marginBottom:18 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#48484a', display:'block', marginBottom:5 }}>
              Password
            </label>
            <div style={{ display:'flex', gap:8 }}>
              <input type="text" value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ ...inputStyle, fontFamily:'"SF Mono",monospace', letterSpacing:.5 }}/>
              <button onClick={() => setPassword(generatePassword())} style={{
                padding:'10px 14px', borderRadius:12, border:'1.5px solid rgba(0,0,0,.1)',
                background:'#fff', color:'#007AFF', fontSize:12, fontWeight:600,
                cursor:'pointer', fontFamily:'inherit', flexShrink:0,
              }}>🔄 New</button>
            </div>
            <div style={{ fontSize:10, color:'#8E8E93', marginTop:4 }}>
              Min 8 chars · upper + lower + digit + special
            </div>
          </div>
          )}
          {isCE && (
          <div style={{ marginBottom:18, padding:'10px 14px', borderRadius:12,
            background:'rgba(52,199,89,.05)', border:'1px solid rgba(52,199,89,.15)' }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#248A3D', marginBottom:2 }}>
              🔐 Password auto-generated
            </div>
            <div style={{ fontSize:10, color:'#8E8E93' }}>
              A strong secure password will be created automatically and shown after account creation.
            </div>
          </div>
          )}

          {/* Permissions — only service_manager needs permission selection; admin/CE/field have fixed/none */}
          {selectedRole !== 'admin' && selectedRole !== 'customer_engineer' && selectedRole !== 'field_engineer' && (
          <div style={{ marginBottom:18 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#48484a', display:'block', marginBottom:8 }}>
              Permissions *
            </label>
            {selectedRole === 'customer_engineer' && (
              <div style={{ fontSize:11, color:'#8E8E93', marginBottom:10, padding:'8px 12px',
                background:'rgba(255,159,10,.06)', borderRadius:10, border:'1px solid rgba(255,159,10,.15)' }}>
                ⚠️ Support Engineers can only have Manage Coupons & Recharge permissions
              </div>
            )}
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {(selectedRole === 'customer_engineer'
                ? ALL_PERMISSIONS.filter(p => CE_PERMISSIONS.includes(p.id))
                : ALL_PERMISSIONS
              ).map(p => {
                const checked = selectedPerms.includes(p.id);
                const isCELocked = selectedRole === 'customer_engineer';
                return (
                  <div key={p.id} onClick={() => !isCELocked && togglePerm(p.id)} style={{
                    display:'flex', alignItems:'center', gap:12,
                    padding:'12px 14px', borderRadius:14, cursor: isCELocked ? 'default' : 'pointer',
                    border: checked ? `2px solid ${p.color}` : '1.5px solid rgba(0,0,0,.08)',
                    background: checked ? `${p.color}08` : '#fafafa',
                    transition:'all .15s',
                    opacity: isCELocked ? 0.85 : 1,
                  }}>
                    <div style={{
                      width:22, height:22, borderRadius:7, flexShrink:0,
                      border: checked ? `2px solid ${p.color}` : '2px solid rgba(0,0,0,.15)',
                      background: checked ? p.color : 'transparent',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      transition:'all .15s',
                    }}>
                      {checked && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12l5 5L19 7" stroke="#fff" strokeWidth="3"
                            strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ fontSize:18, flexShrink:0 }}>{p.icon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight: checked ? 700 : 500,
                        color: checked ? '#1c1c1e' : '#48484a' }}>{p.label}</div>
                      <div style={{ fontSize:10, color:'#8E8E93', marginTop:1 }}>{p.desc}</div>
                    </div>
                    {isCELocked && <span style={{ fontSize:9, color:'#8E8E93', fontWeight:600 }}>Fixed</span>}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {error && !error.toLowerCase().includes('password') && (
            <div style={{
              padding:'10px 14px', borderRadius:11, marginBottom:14,
              background:'rgba(255,59,48,.08)', border:'1px solid rgba(255,59,48,.2)',
              fontSize:12, color:'#FF3B30', fontWeight:600,
              display:'flex', alignItems:'center', gap:8,
            }}>
              ⚠️ {error}
            </div>
          )}

          <button onClick={handleCreate} disabled={creating} style={{
            width:'100%', padding:'14px', borderRadius:14, border:'none',
            background: creating ? '#c7c7cc' : 'linear-gradient(135deg,#DC2626,#B91C1C)',
            color:'#fff', fontWeight:700, fontSize:15,
            cursor: creating ? 'not-allowed' : 'pointer', fontFamily:'inherit',
            boxShadow: creating ? 'none' : '0 4px 18px rgba(220,38,38,.4)',
            display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          }}>
            {creating ? (
              <>
                <div style={{ width:16, height:16, borderRadius:'50%',
                  border:'2px solid rgba(255,255,255,.3)', borderTopColor:'#fff',
                  animation:'spinAnim .7s linear infinite' }}/>
                Creating…
              </>
            ) : `✅ Create ${isCE ? 'Field Engineer' : selectedRole === 'admin' ? 'Admin' : roleLabel(selectedRole)} Account`}
          </button>
        </div>

        {/* ── RIGHT: Existing Accounts List ── */}
        <div style={{
          background:'#fff', borderRadius:20, padding:'24px',
          border:'1px solid rgba(0,0,0,.06)',
          boxShadow:'0 2px 16px rgba(0,0,0,.07)',
          animation:'fadeUp .35s ease .1s both',
        }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{
                width:36, height:36, borderRadius:11,
                background:'linear-gradient(135deg,#007AFF,#0056d3)',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
              }}>📋</div>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:'#1c1c1e' }}>Existing Accounts</div>
                <div style={{ fontSize:11, color:'#8E8E93' }}>
                  {accounts.length} account{accounts.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            <button onClick={fetchAccounts} disabled={loadingAccounts} style={{
              padding:'6px 12px', borderRadius:8, border:'1px solid rgba(0,0,0,.1)',
              background:'#fff', color:'#007AFF', fontSize:11, fontWeight:600,
              cursor:'pointer', fontFamily:'inherit',
            }}>{loadingAccounts ? 'Loading…' : '↻ Refresh'}</button>
          </div>

          {accounts.length === 0 ? (
            <div style={{ textAlign:'center', padding:'32px', color:'#8E8E93' }}>
              <div style={{ fontSize:36, marginBottom:10 }}>👤</div>
              <div style={{ fontSize:14, fontWeight:600, color:'#3a3a3c', marginBottom:4 }}>
                No accounts yet
              </div>
              <div style={{ fontSize:12 }}>Create one using the form</div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {accounts.filter(a => creatableRoles.includes(a.role)).map((acc, idx) => {
                const rt = ROLE_TYPES.find(r => r.id === acc.role);
                return (
                <div key={acc.email || idx} style={{
                  padding:'14px 16px', borderRadius:14,
                  border:'1px solid rgba(0,0,0,.06)',
                  background:'#fafafa',
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                    <div style={{
                      width:34, height:34, borderRadius:10,
                      background:'linear-gradient(135deg,#DC2626,#EF4444)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      color:'#fff', fontSize:13, fontWeight:800, flexShrink:0,
                    }}>
                      {(acc.fullName || acc.email || '?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>
                        {acc.fullName || acc.email}
                      </div>
                      <div style={{ fontSize:11, color:'#8E8E93', fontFamily:'monospace' }}>{acc.email}</div>
                    </div>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:20,
                      background:'rgba(52,199,89,.1)', color:'#34C759', whiteSpace:'nowrap',
                    }}>
                      {rt ? `${rt.icon} ${rt.label.toUpperCase()}` : (acc.role || '').toUpperCase()}
                    </span>
                  </div>

                  {/* Permission pills */}
                  {acc.permissions && acc.permissions.length > 0 && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:4 }}>
                      {acc.permissions.map(pId => {
                        const p = PERMISSIONS.find(x => x.id === pId);
                        return (
                          <span key={pId} style={{
                            fontSize:9, fontWeight:600, padding:'2px 8px', borderRadius:20,
                            background: p ? `${p.color}10` : 'rgba(0,0,0,.05)',
                            color: p ? p.color : '#8E8E93',
                          }}>
                            {p ? `${p.icon} ${p.label}` : pId}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
                    {acc.createdAt && (
                      <span style={{ fontSize:10, color:'#aeaeb2' }}>
                        Created: {new Date(acc.createdAt).toLocaleDateString('en-IN',{
                          day:'2-digit', month:'short', year:'numeric'
                        })}
                      </span>
                    )}
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => handleRemoveManager(acc.email)} style={{
                        padding:'5px 12px', borderRadius:8, border:'none',
                        background:'rgba(255,59,48,.08)', color:'#FF3B30',
                        fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                        display:'flex', alignItems:'center', gap:4,
                      }}>
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Team Coupon — only for Service Managers (not Support Engineers or Admins) */}
      {!isAdmin && role === 'service_manager' && <MonthlyCouponPanel session={session}/>}

      {/* Success Popup */}
      {successData && (
        <SuccessPopup
          email={successData.email}
          password={successData.password}
          permissions={successData.permissions}
          needsConfirm={successData.needsConfirm}
          roleLabel={successData.roleLabel}
          onClose={() => setSuccessData(null)}
        />
      )}

      {/* Confirm Email Modal */}
      {confirmEmail && !successData && (
        <ConfirmModal
          email={confirmEmail}
          onConfirmed={handleConfirmed}
          onClose={() => setConfirmEmail(null)}
        />
      )}
    </div>
  );
}
